/**
 * Builders for schema.org JSON-LD objects, rendered via
 * components/Utlility/JsonLd.js. Pure functions over already-resolved values —
 * no Redux or pricing imports here; pages pass in what they display.
 *
 * aggregateRating/review are deliberately absent: no product-level rating data
 * exists client-side (services/rating.js only submits ratings). Revisit if a
 * ratings aggregate endpoint ever lands.
 */

// Matches BASE_URL in constants/SeoConfig.js.
const BASE_URL = "https://game.city";

/** Prefix site-relative paths (e.g. storeSlug "/shop/foo") with the domain. */
const absoluteUrl = (url) => {
    if (!url) return undefined;
    return url.startsWith("http") ? url : `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

/**
 * Product markup for a purchasable game / game pack.
 * Returns null when name or price is missing (page still loading).
 */
export const productJsonLd = ({ name, description, images, sku, price, currency, url }) => {
    if (!name || price == null) return null;
    const absUrl = absoluteUrl(url);
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name,
        ...(description && { description }),
        ...(sku && { sku }),
        image: (images || []).filter(Boolean),
        brand: { "@type": "Brand", name: "Game.city" },
        ...(absUrl && { url: absUrl }),
        offers: {
            "@type": "Offer",
            price,
            priceCurrency: currency,
            availability: "https://schema.org/InStock",
            ...(absUrl && { url: absUrl }),
        },
    };
};

/** items: [{ name, url }] in order from site root to current page. */
export const breadcrumbJsonLd = (items) => {
    const valid = (items || []).filter((i) => i?.name);
    if (valid.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: valid.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            ...(item.url && { item: absoluteUrl(item.url) }),
        })),
    };
};

/** items: [{ name, url }] — e.g. the shop catalog for the /shop index. */
export const itemListJsonLd = (items) => {
    const valid = (items || []).filter((i) => i?.name && i?.url);
    if (valid.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: valid.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            url: absoluteUrl(item.url),
        })),
    };
};
